'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertCircle, FeatherCheckCircle, FeatherClock, FeatherCreditCard, FeatherExternalLink, FeatherLoader, FeatherMapPin, FeatherRotateCcw, FeatherShield } from '@subframe/core';
import { Alert, Badge, Button } from '@/shared/components/ui';
import { formatDataPrazo, formatDate } from '@/shared/formatters';
import { ehZero, formatarValorDecimal } from '@/shared/formatters/valorDecimal';
import { enderecoEmLinha } from '@/shared/utils/enderecoAr';
import type { AutorizacaoArEnvio } from '@/types/arbitral/envioArTypes';
interface AutorizacaoArCardProps {
  envio: AutorizacaoArEnvio;
  vencido: boolean;
  popupBloqueado: boolean;
  /** Decisão desta solicitação já no ar — o botão dela fica travado até o desfecho. */
  acaoEmVoo: boolean;
  onAutorizarConvenio: () => void;
  onPagar: () => void;
  onConfirmarPagamento: () => void;
  onVoltar: () => void;
  onCorrigirEndereco: () => void;
  onRecusar: () => void;
  onDesfazerRecusa: () => void;
}
function BlocoEndereco(t0) {
  const $ = _c(18);
  const {
    envio,
    podeCorrigir,
    acaoEmVoo,
    onCorrigirEndereco
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FeatherMapPin className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-caption-bold font-caption-bold text-subtext-color">A carta será entregue em</span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== envio.endereco) {
    t3 = enderecoEmLinha(envio.endereco);
    $[2] = envio.endereco;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="text-body font-body text-default-font">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== envio.endereco.cep) {
    t5 = <span className="text-caption font-caption text-subtext-color">CEP {envio.endereco.cep} · endereço conferido pela secretaria</span>;
    $[6] = envio.endereco.cep;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className="flex items-start gap-2">{t1}<div className="flex flex-col gap-0.5">{t2}{t4}{t5}</div></div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== acaoEmVoo || $[12] !== onCorrigirEndereco || $[13] !== podeCorrigir) {
    t7 = podeCorrigir && <div className="flex justify-start"><Button variant="neutral-tertiary" size="small" disabled={acaoEmVoo} onClick={onCorrigirEndereco}>Corrigir endereço</Button></div>;
    $[11] = acaoEmVoo;
    $[12] = onCorrigirEndereco;
    $[13] = podeCorrigir;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <div className="flex flex-col gap-2 rounded-[8px] bg-white/70 px-3 py-3">{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
export function AutorizacaoArCard(t0) {
  const $ = _c(130);
  const {
    envio,
    vencido,
    popupBloqueado,
    acaoEmVoo,
    onAutorizarConvenio,
    onPagar,
    onConfirmarPagamento,
    onVoltar,
    onCorrigirEndereco,
    onRecusar,
    onDesfazerRecusa
  } = t0;
  const conveniado = envio.cobranca === "CONVENIO";
  const emEspera = envio.estado === "PROCESSANDO" || envio.estado === "VERIFICANDO";
  const decidindo = envio.estado === "AGUARDANDO_AUTORIZACAO";
  let t1;
  if ($[0] !== envio.acoes) {
    t1 = envio.acoes.includes("AUTORIZAR");
    $[0] = envio.acoes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const podeAutorizar = t1;
  let t2;
  if ($[2] !== envio.acoes) {
    t2 = envio.acoes.includes("VERIFICAR_PAGAMENTO");
    $[2] = envio.acoes;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const podeVerificar = t2;
  let t3;
  if ($[4] !== envio.acoes) {
    t3 = envio.acoes.includes("DEVOLVER_ENDERECO");
    $[4] = envio.acoes;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const podeCorrigir = t3;
  let t4;
  if ($[6] !== envio.acoes) {
    t4 = envio.acoes.includes("RECUSAR");
    $[6] = envio.acoes;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const podeRecusar = t4;
  let t5;
  if ($[8] !== envio.acoes) {
    t5 = envio.acoes.includes("DESFAZER_RECUSA");
    $[8] = envio.acoes;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const podeDesfazerRecusa = t5;
  const emitindoCobranca = envio.situacaoCobranca === "EMITINDO";
  let t6;
  if ($[10] !== emitindoCobranca || $[11] !== envio.acoes) {
    t6 = envio.acoes.includes("PAGAR") && !emitindoCobranca;
    $[10] = emitindoCobranca;
    $[11] = envio.acoes;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const podePagar = t6;
  let t7;
  if ($[13] !== envio.valores.valorModalidade) {
    t7 = ehZero(envio.valores.valorModalidade);
    $[13] = envio.valores.valorModalidade;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const temAdicionalDeModalidade = !t7;
  if (envio.estado === "ERRO_EMISSAO") {
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="bg-default-background flex w-full flex-col gap-4 rounded-[16px] border border-solid border-neutral-200 p-6"><Alert variant="warning" icon={<FeatherAlertCircle />} title={"N\xE3o conseguimos emitir a cobran\xE7a do envio"} description={"A secretaria j\xE1 foi avisada e vai reemitir. Voc\xEA n\xE3o precisa fazer nada por enquanto."} /></div>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  if (envio.estado === "ENDERECO_EM_CORRECAO") {
    let t8;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Alert variant="warning" icon={<FeatherMapPin />} title={"Endere\xE7o devolvido para corre\xE7\xE3o"} description={"Nada foi cobrado. A secretaria vai revisar o endere\xE7o e enviar uma nova solicita\xE7\xE3o de envio."} />;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== envio.observacaoRequerente) {
      t9 = envio.observacaoRequerente && <p className="text-caption font-caption text-warning-800 italic">“{envio.observacaoRequerente}”</p>;
      $[17] = envio.observacaoRequerente;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== t9) {
      t10 = <div className="bg-warning-50 border-warning-200 flex w-full flex-col gap-3 rounded-[16px] border border-solid p-6">{t8}{t9}</div>;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  if (envio.estado === "RECUSADO") {
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <FeatherAlertCircle />;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    const t9 = `A citação de ${envio.requeridoNome} continua pendente. A secretaria foi avisada e vai avaliar o próximo passo do processo.`;
    let t10;
    if ($[22] !== t9) {
      t10 = <Alert variant="error" icon={t8} title={"Voc\xEA recusou o envio da carta"} description={t9} />;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== envio.observacaoRequerente) {
      t11 = envio.observacaoRequerente && <p className="text-caption font-caption text-error-800 italic">“{envio.observacaoRequerente}”</p>;
      $[24] = envio.observacaoRequerente;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== acaoEmVoo || $[27] !== onDesfazerRecusa || $[28] !== podeDesfazerRecusa) {
      t12 = podeDesfazerRecusa && <div className="flex justify-start"><Button variant="neutral-secondary" size="medium" icon={<FeatherRotateCcw />} disabled={acaoEmVoo} onClick={onDesfazerRecusa}>Desfazer recusa</Button></div>;
      $[26] = acaoEmVoo;
      $[27] = onDesfazerRecusa;
      $[28] = podeDesfazerRecusa;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12) {
      t13 = <div className="bg-error-50 border-error-200 flex w-full flex-col gap-3 rounded-[16px] border border-solid p-6">{t10}{t11}{t12}</div>;
      $[30] = t10;
      $[31] = t11;
      $[32] = t12;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    return t13;
  }
  if (envio.estado === "PAGO" || envio.estado === "AUTORIZADO_CONVENIO") {
    let t8;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="bg-success-100 flex h-10 w-10 shrink-0 items-center justify-center rounded-full"><FeatherCheckCircle className="text-success-700 h-5 w-5" /></div>;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    const t9 = envio.estado === "PAGO" ? "Envio pago e liberado" : "Envio autorizado";
    let t10;
    if ($[35] !== t9) {
      t10 = <p className="text-body-bold font-body-bold text-success-900">{t9}</p>;
      $[35] = t9;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    let t11;
    if ($[37] !== envio.estado || $[38] !== envio.pagoEm || $[39] !== envio.requeridoNome || $[40] !== envio.valores.valorTotal) {
      t11 = envio.estado === "PAGO" ? `Pagamento de ${formatarValorDecimal(envio.valores.valorTotal)} confirmado${envio.pagoEm ? ` em ${formatDate(envio.pagoEm)}` : ""}. A carta para ${envio.requeridoNome} entrou na fila de postagem.` : `O valor de ${formatarValorDecimal(envio.valores.valorTotal)} entra na fatura do seu ciclo. A carta para ${envio.requeridoNome} entrou na fila de postagem.`;
      $[37] = envio.estado;
      $[38] = envio.pagoEm;
      $[39] = envio.requeridoNome;
      $[40] = envio.valores.valorTotal;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    let t12;
    if ($[42] !== t11) {
      t12 = <p className="text-caption font-caption text-success-800">{t11}</p>;
      $[42] = t11;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    let t13;
    if ($[44] !== t10 || $[45] !== t12) {
      t13 = <div className="bg-success-50 border-success-200 flex w-full items-start gap-3 rounded-[16px] border border-solid p-6">{t8}<div className="flex flex-col gap-1">{t10}{t12}</div></div>;
      $[44] = t10;
      $[45] = t12;
      $[46] = t13;
    } else {
      t13 = $[46];
    }
    return t13;
  }
  const t8 = `flex w-full flex-col gap-4 rounded-[16px] border border-solid p-6 ${vencido ? "bg-error-50 border-error-200" : "bg-brand-50 border-brand-200"}`;
  const t9 = `text-caption-bold font-caption-bold tracking-wider uppercase ${vencido ? "text-error-700" : "text-brand-700"}`;
  let t10;
  if ($[47] !== t9) {
    t10 = <span className={t9}>Citação por carta (AR)</span>;
    $[47] = t9;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  const t11 = `text-heading-1 font-heading-1 ${vencido ? "text-error-900" : "text-brand-900"}`;
  let t12;
  if ($[49] !== envio.valores.valorTotal) {
    t12 = formatarValorDecimal(envio.valores.valorTotal);
    $[49] = envio.valores.valorTotal;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== t11 || $[52] !== t12) {
    t13 = <span className={t11}>{t12}</span>;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== envio.prazoAutorizacaoEm || $[55] !== envio.requeridoNome || $[56] !== vencido) {
    t14 = envio.prazoAutorizacaoEm && <span className={`text-caption font-caption mt-1 flex items-center gap-1.5 ${vencido ? "text-error-700" : "text-brand-700"}`}><FeatherClock className="h-3.5 w-3.5" />{vencido ? `Venceu em ${formatDataPrazo(envio.prazoAutorizacaoEm)}` : `Vence em ${formatDataPrazo(envio.prazoAutorizacaoEm)}`}{" \xB7 requerido "}{envio.requeridoNome}</span>;
    $[54] = envio.prazoAutorizacaoEm;
    $[55] = envio.requeridoNome;
    $[56] = vencido;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  let t15;
  if ($[58] !== t10 || $[59] !== t13 || $[60] !== t14) {
    t15 = <div className="flex flex-col gap-1">{t10}{t13}{t14}</div>;
    $[58] = t10;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  const t16 = conveniado ? "brand" : vencido ? "error" : "warning";
  const t17 = conveniado ? "Conv\xEAnio \xB7 entra na fatura" : "Aguardando sua autoriza\xE7\xE3o";
  let t18;
  if ($[62] !== t16 || $[63] !== t17) {
    t18 = <Badge variant={t16}>{t17}</Badge>;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t15 || $[66] !== t18) {
    t19 = <div className="flex w-full flex-wrap items-start justify-between gap-3">{t15}{t18}</div>;
    $[65] = t15;
    $[66] = t18;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== decidindo || $[69] !== envio.requeridoNome) {
    t20 = decidindo && <div className="rounded-[8px] bg-white/70 px-3 py-2"><span className="text-caption font-caption text-subtext-color">{`Não conseguimos citar ${envio.requeridoNome} por e-mail nem por WhatsApp. Para o processo seguir, a citação precisa ser feita por carta com aviso de recebimento.`}</span></div>;
    $[68] = decidindo;
    $[69] = envio.requeridoNome;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== vencido) {
    t21 = vencido && <div className="flex items-start gap-2 rounded-[8px] bg-white/60 px-3 py-2"><FeatherAlertCircle className="text-error-600 mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="text-caption font-caption text-error-700">O prazo para responder venceu, mas a citação continua pendente — você ainda pode autorizar o envio.</span></div>;
    $[71] = vencido;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== acaoEmVoo || $[74] !== envio || $[75] !== onCorrigirEndereco || $[76] !== podeCorrigir) {
    t22 = <BlocoEndereco envio={envio} podeCorrigir={podeCorrigir} acaoEmVoo={acaoEmVoo} onCorrigirEndereco={onCorrigirEndereco} />;
    $[73] = acaoEmVoo;
    $[74] = envio;
    $[75] = onCorrigirEndereco;
    $[76] = podeCorrigir;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== conveniado || $[79] !== decidindo || $[80] !== envio.valores.valorEnvio || $[81] !== envio.valores.valorModalidade || $[82] !== temAdicionalDeModalidade) {
    t23 = !conveniado && decidindo && <div className="flex flex-col gap-1 rounded-[8px] bg-white/70 px-3 py-2"><span className="text-caption font-caption text-default-font">{`Envio pelo CEP ${formatarValorDecimal(envio.valores.valorEnvio)}`}{temAdicionalDeModalidade && ` + entrega em mãos próprias ${formatarValorDecimal(envio.valores.valorModalidade)}`}</span>{temAdicionalDeModalidade && <span className="text-caption font-caption text-subtext-color">Em mãos próprias, só o próprio requerido pode receber e assinar.</span>}</div>;
    $[78] = conveniado;
    $[79] = decidindo;
    $[80] = envio.valores.valorEnvio;
    $[81] = envio.valores.valorModalidade;
    $[82] = temAdicionalDeModalidade;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== conveniado || $[85] !== decidindo) {
    t24 = conveniado && decidindo && <div className="rounded-[8px] bg-white/70 px-3 py-2"><span className="text-caption font-caption text-subtext-color">Seu escritório é conveniado: autorize o envio e o valor entra no fechamento do ciclo, sem cobrança agora.</span></div>;
    $[84] = conveniado;
    $[85] = decidindo;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== decidindo || $[88] !== emitindoCobranca) {
    t25 = emitindoCobranca && decidindo && <div className="flex items-center gap-3 rounded-[8px] bg-white/70 px-3 py-3"><FeatherLoader className="text-brand-600 h-5 w-5 animate-spin" /><div className="flex flex-col"><span className="text-body-bold font-body-bold text-default-font">Preparando a cobrança…</span><span className="text-caption font-caption text-subtext-color">Assim que o link estiver pronto, o botão de pagar aparece aqui — esta tela atualiza sozinha. Você já pode corrigir o endereço ou recusar o envio.</span></div></div>;
    $[87] = decidindo;
    $[88] = emitindoCobranca;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== decidindo || $[91] !== popupBloqueado) {
    t26 = popupBloqueado && decidindo && <div className="border-error-200 bg-error-50 flex items-start gap-2 rounded-[8px] border border-solid px-3 py-2"><FeatherAlertCircle className="text-error-600 mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="text-caption font-caption text-error-700">O navegador bloqueou a aba do checkout. Nada foi cobrado — libere pop-ups para este site ou tente abrir de novo.</span></div>;
    $[90] = decidindo;
    $[91] = popupBloqueado;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== envio.estado) {
    t27 = envio.estado === "VERIFICANDO" && <div className="flex items-center gap-3 rounded-[8px] bg-white/70 px-3 py-3"><FeatherLoader className="text-brand-600 h-5 w-5 animate-spin" /><div className="flex flex-col"><span className="text-body-bold font-body-bold text-default-font">Confirmando com o Asaas…</span><span className="text-caption font-caption text-subtext-color">Isso leva alguns segundos. Não feche esta tela.</span></div></div>;
    $[93] = envio.estado;
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== acaoEmVoo || $[96] !== envio.estado || $[97] !== onConfirmarPagamento || $[98] !== onPagar || $[99] !== onVoltar || $[100] !== podePagar || $[101] !== podeVerificar) {
    t28 = envio.estado === "PROCESSANDO" && <div className="flex flex-col gap-3 rounded-[8px] bg-white/70 px-3 py-3"><div className="flex flex-col gap-0.5"><span className="text-body-bold font-body-bold text-default-font">Aguardando o pagamento no Asaas</span><span className="text-caption font-caption text-subtext-color">Conclua o pagamento na aba que abrimos. Pix confirma em minutos; boleto compensa em até 3 dias úteis. Assim que o Asaas confirmar, esta tela atualiza sozinha.</span></div><div className="flex flex-wrap items-center gap-2">{podeVerificar && <Button variant="brand-primary" size="medium" disabled={acaoEmVoo} onClick={onConfirmarPagamento}>Já paguei — verificar agora</Button>}{podePagar && <Button variant="neutral-secondary" size="medium" iconRight={<FeatherExternalLink />} onClick={onPagar}>Reabrir checkout</Button>}<Button variant="neutral-tertiary" size="medium" className="ml-auto" onClick={onVoltar}>Ainda não vou pagar</Button></div></div>;
    $[95] = acaoEmVoo;
    $[96] = envio.estado;
    $[97] = onConfirmarPagamento;
    $[98] = onPagar;
    $[99] = onVoltar;
    $[100] = podePagar;
    $[101] = podeVerificar;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  let t29;
  if ($[103] !== acaoEmVoo || $[104] !== conveniado || $[105] !== decidindo || $[106] !== onAutorizarConvenio || $[107] !== onPagar || $[108] !== onRecusar || $[109] !== podeAutorizar || $[110] !== podePagar || $[111] !== podeRecusar) {
    t29 = decidindo && (podeAutorizar || podePagar || podeRecusar) && <div className="flex flex-wrap items-center gap-2">{conveniado ? podeAutorizar && <Button variant="brand-primary" size="large" icon={<FeatherCheckCircle />} disabled={acaoEmVoo} onClick={onAutorizarConvenio}>Autorizar envio</Button> : podePagar && <Button variant="brand-primary" size="large" icon={<FeatherCreditCard />} iconRight={<FeatherExternalLink />} disabled={acaoEmVoo} onClick={onPagar}>Aprovar e pagar envio</Button>}{podeRecusar && <Button variant="neutral-tertiary" size="medium" className="ml-auto" disabled={acaoEmVoo} onClick={onRecusar}>Não quero enviar</Button>}</div>;
    $[103] = acaoEmVoo;
    $[104] = conveniado;
    $[105] = decidindo;
    $[106] = onAutorizarConvenio;
    $[107] = onPagar;
    $[108] = onRecusar;
    $[109] = podeAutorizar;
    $[110] = podePagar;
    $[111] = podeRecusar;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  let t30;
  if ($[113] !== conveniado || $[114] !== emEspera) {
    t30 = !conveniado && !emEspera && <div className="flex items-start gap-2 rounded-[8px] bg-white/50 px-3 py-2"><FeatherShield className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-500" /><span className="text-caption font-caption text-subtext-color">Pix, boleto ou cartão — você escolhe no checkout seguro do Asaas. Com o pagamento confirmado, a carta é liberada para postagem.</span></div>;
    $[113] = conveniado;
    $[114] = emEspera;
    $[115] = t30;
  } else {
    t30 = $[115];
  }
  let t31;
  if ($[116] !== t19 || $[117] !== t20 || $[118] !== t21 || $[119] !== t22 || $[120] !== t23 || $[121] !== t24 || $[122] !== t25 || $[123] !== t26 || $[124] !== t27 || $[125] !== t28 || $[126] !== t29 || $[127] !== t30 || $[128] !== t8) {
    t31 = <div className={t8}>{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}{t30}</div>;
    $[116] = t19;
    $[117] = t20;
    $[118] = t21;
    $[119] = t22;
    $[120] = t23;
    $[121] = t24;
    $[122] = t25;
    $[123] = t26;
    $[124] = t27;
    $[125] = t28;
    $[126] = t29;
    $[127] = t30;
    $[128] = t8;
    $[129] = t31;
  } else {
    t31 = $[129];
  }
  return t31;
}